import React, { useEffect, useMemo, useState } from "react";

/**
 * DhikrCounter
 * - Tap: +1
 * - boutons + / reset (default)
 * - Variante "sleep" : compteur à gauche, reset à droite, gros + au centre
 * - Sauvegarde localStorage PAR compteur (clé unique)
 * - ✅ Option dailyReset : reset automatique au nouveau jour (heure locale)
 */
export default function DhikrCounter({
  counterId,
  id,
  label = "",
  target = 33,
  storagePrefix = "dhikr_counts_v1",
  onDoneChange,
  className = "",
  variant = "default",
  dailyReset = false, // ✅ NEW
}) {
  const resolvedId = counterId ?? id;

  const storageKey = useMemo(() => {
    const safeId = resolvedId ? String(resolvedId) : "missing-id";
    return `${storagePrefix}:${safeId}`;
  }, [storagePrefix, resolvedId]);

  const getLocalDateKey = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const dayKeyStorage = useMemo(() => `${storageKey}:day`, [storageKey]);

  const readStoredCount = () => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const n = raw ? Number(raw) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const [count, setCount] = useState(readStoredCount);

  const ensureDailyReset = () => {
    if (!dailyReset) return;
    if (typeof window === "undefined") return;
    try {
      const today = getLocalDateKey();
      const last = window.localStorage.getItem(dayKeyStorage);
      if (last !== today) {
        window.localStorage.setItem(dayKeyStorage, today);
        setCount(0);
        window.localStorage.setItem(storageKey, "0");
      }
    } catch {}
  };

  // ✅ Recharge compteur quand on change de dhikr + reset quotidien (si activé)
  useEffect(() => {
    setCount(readStoredCount());
    ensureDailyReset();

    const onFocus = () => ensureDailyReset();
    const onVisibility = () => {
      if (!document.hidden) ensureDailyReset();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, dailyReset]);

  // ✅ Sauvegarde + done
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(storageKey, String(count));
      } catch {}
    }
    if (typeof onDoneChange === "function") onDoneChange(count >= target);
  }, [count, target, storageKey, onDoneChange]);

  const done = count >= target;

  const inc = () => setCount((c) => Math.min(target, c + 1));
  const reset = () => setCount(0);

  // ✅ Variante SLEEP : design spécifique
  if (variant === "sleep") {
    const gold = "rgba(217,180,106,0.95)";
    const goldBorder = "rgba(217,180,106,0.55)";
    const goldBg = "rgba(217,180,106,0.10)";

    return (
      <div
        className={`dhikr-counter ${done ? "is-done" : ""} ${className}`}
        style={{ padding: 12 }}
      >
        {/* Top row: compteur à gauche + reset à droite */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span className="dhikr-progress" style={{ fontWeight: 900, color: gold }}>
            {count}/{target}
          </span>

          <button
            type="button"
            className="dhikr-btn dhikr-reset"
            onClick={reset}
            disabled={count === 0}
            style={{
              height: 46,
              padding: "0 16px",
              borderRadius: 999,
              border: `1px solid ${goldBorder}`,
              background: goldBg,
              color: gold,
              fontWeight: 900,
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            Reset
          </button>
        </div>

        {/* Gros bouton + au milieu */}
        <button
          type="button"
          onClick={inc}
          disabled={done}
          aria-label={`${label || "Dhikr"} ajouter 1`}
       
          style={{
            width: "100%",
            height: 64,
            marginTop: 10,
            borderRadius: 18,
            fontSize: "2rem",
            fontWeight: 900,
            border: `1px solid ${goldBorder}`,
            background: goldBg,
            color: gold,
            cursor: done ? "not-allowed" : "pointer",
            opacity: done ? 0.45 : 1,
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          +
        </button>

        {label ? (
          <div
            style={{
              marginTop: 10,
              textAlign: "center",
              fontWeight: 900,
              opacity: 0.92,
              color: gold,
            }}
          >
            {label}
          </div>
        ) : null}
      </div>
    );
  }

  // ✅ Default : rendu existant (tap + 2 boutons) — sans "-"
  return (
    <div className={`dhikr-counter ${done ? "is-done" : ""} ${className}`}>
      <button
        type="button"
        className="dhikr-tap"
        onClick={inc}
        aria-label={`${label || "Dhikr"} ajouter 1`}
        title="Tap = +1"
      >
        <span className="dhikr-label">{label}</span>
        <span className="dhikr-progress">
          {count}/{target}
        </span>
      </button>

      <div className="dhikr-actions">
        <button type="button" className="dhikr-btn" onClick={inc} disabled={done}>
          +
        </button>

        <button
          type="button"
          className="dhikr-btn dhikr-reset"
          onClick={reset}
          disabled={count === 0}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
